import React, { useEffect, useState, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import client from '../../../lib/feathers';

/**
 * Pending Approval view — lists full-chain runs that finished their
 * read-only stages and produced a proposed plan, but haven't been
 * approved for the prod-writing stages yet.
 *
 * Each row shows:
 *   - listing name
 *   - when the chain ran
 *   - stage breakdown (01/02/02c always; 03/04 dry-run proposals)
 *   - the proposal text from stage 03 + 04 in a pre block
 *   - "Approve" button that PATCHes approvedAt + approvedByUserId
 *
 * The supervisor's next tick picks up approved rows and re-runs
 * stages 03 + 04 with the prod token. Until approved, the row sits
 * here indefinitely.
 */
export default function ListingDataHealthPendingApproval() {
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      // Just filter on outcome — once an approved row is claimed by
      // the supervisor its outcome flips out of 'awaiting_review' and
      // the row naturally drops out of the result. (Filtering on
      // `approvedAt: null` here breaks: feathers-rest serializes the
      // literal `null` as the string "null" which Postgres can't coerce
      // to a timestamp → "invalid input syntax for type timestamp".)
      const result = await client.service('admin/listing-enrichment-run').find({
        query: {
          outcome: 'awaiting_review',
          $sort: { finishedAt: 1 },
          $limit: 50,
        },
      });
      // Filter out already-approved rows on the client. They stay in
      // outcome='awaiting_review' until the supervisor's next tick
      // re-claims them (~5-15 min) and would otherwise clutter the
      // "needs your eyes" view. (Can't filter server-side with
      // `approvedAt: null` — feathers-rest serializes null as the
      // string "null" and Postgres 500s on timestamp coercion.)
      const unapproved = (result.data || []).filter((r) => !r.approvedAt);
      setRuns(unapproved);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load pending-approval queue');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (runId) => {
    if (!window.confirm('Approve this run? The supervisor will write the proposed changes to prod on its next tick.')) return;
    setBusyId(runId);
    try {
      // Server stamps approvedByUserId from auth context — we just
      // send the timestamp. Avoids the prod bug where me.id from
      // Redux was sometimes undefined and the patch silently no-op'd.
      const patched = await client.service('admin/listing-enrichment-run').patch(runId, {
        approvedAt: new Date().toISOString(),
      });
      // Verify the server actually persisted the approval before
      // removing from the list. If approvedAt didn't stick, surface
      // an error instead of pretending it worked.
      if (!patched || !patched.approvedAt) {
        throw new Error('approvedAt did not persist on the server — patch was a no-op');
      }
      setRuns((cur) => (cur || []).filter((r) => r.id !== runId));
    } catch (e) {
      alert(`Approve failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  // Reject keeps the proposal text on the row for audit but moves the
  // row out of awaiting_review so it stops cluttering the queue. The
  // outcome flips to 'skipped' with an explanatory error. (Not adding
  // a new 'rejected' enum value — overloaded 'skipped' works fine and
  // avoids a migration.)
  const reject = async (runId) => {
    const reason = window.prompt('Reject this proposal? Add a short reason (will be saved on the row):', 'admin rejected');
    if (reason === null) return;  // operator cancelled the prompt
    setBusyId(runId);
    try {
      await client.service('admin/listing-enrichment-run').patch(runId, {
        outcome: 'skipped',
        error: `admin rejected: ${reason || 'no reason given'}`,
        finishedAt: new Date().toISOString(),
      });
      setRuns((cur) => (cur || []).filter((r) => r.id !== runId));
    } catch (e) {
      alert(`Reject failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Page title="Reports">
      <div className="pb-5 border-b border-gray-200">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[
            { name: 'Reports', href: '/admin/reports' },
            { name: 'Listing Data Health', href: '/admin/reports/listing-data-health' },
            { name: 'Pending Approval' },
          ]} />
        </nav>
      </div>

      <div className="py-5">
        <h1 className="text-2xl pb-3">Pending Approval</h1>
        <p className="text-sm text-gray-500 max-w-3xl">
          Full-chain runs that finished their read-only stages (crawl / extract / normalize) and produced a proposed plan for stages 03 + 04 (the prod-writing ones). Review the proposal and click <strong>Approve</strong> to let the supervisor run the writes on its next tick. The row stays here indefinitely until approved.
        </p>
      </div>

      {error && <div className="text-sm text-red-700 mb-4">{error}</div>}
      {!error && runs === null && <div className="text-sm text-gray-500">Loading…</div>}
      {!error && runs && runs.length === 0 && (
        <div className="bg-white shadow rounded-lg p-6 text-sm text-gray-500 italic">
          Nothing waiting for approval. New full-chain runs land here after their read-only stages complete.
        </div>
      )}

      {!error && runs && runs.length > 0 && (
        <div className="space-y-4">
          {runs.map((r) => {
            const stages = (r.stages && typeof r.stages === 'object') ? r.stages : {};
            const proposal03 = stages['03_import']?.proposal;
            const proposal04 = stages['04_upload_assets']?.proposal;
            return (
              <div key={r.id} className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-start gap-4">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      {r.listing ? (
                        <NavLink to={`/admin/listings/${r.listing.id}`} className="text-indigo-700 hover:underline">
                          {r.listing.name}
                        </NavLink>
                      ) : (
                        <span className="text-gray-400 italic">listing unknown</span>
                      )}
                    </h3>
                    <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-gray-500">
                      <span>
                        Ran {r.finishedAt ? formatDistanceToNow(new Date(r.finishedAt), { addSuffix: true }) : '—'}
                      </span>
                      <span>· trigger {r.triggeredBy}</span>
                      <span>· id <code>{r.id.slice(0, 8)}</code></span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => reject(r.id)}
                      disabled={busyId === r.id}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                      title="Drop this proposed run without writing. Outcome becomes 'skipped' with your reason on the row."
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={() => approve(r.id)}
                      disabled={busyId === r.id}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700 disabled:opacity-50"
                    >
                      {busyId === r.id ? 'Approving…' : 'Approve write'}
                    </button>
                  </div>
                </div>

                <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Stage 03 — Import wines (proposed)</h4>
                    {proposal03 ? (
                      <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap max-h-64">
                        {proposal03}
                      </pre>
                    ) : (
                      <div className="text-xs text-gray-400 italic">no proposal captured</div>
                    )}
                  </div>
                  <div>
                    <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Stage 04 — Upload assets (proposed)</h4>
                    {proposal04 ? (
                      <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap max-h-64">
                        {proposal04}
                      </pre>
                    ) : (
                      <div className="text-xs text-gray-400 italic">no proposal captured</div>
                    )}
                  </div>
                </div>

                {/* Stage breakdown: ok/fail per upstream stage, for context */}
                <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs flex flex-wrap gap-2">
                  {Object.entries(stages).map(([k, v]) => (
                    <span key={k} className={`px-2 py-0.5 rounded font-mono ${v?.ok === true ? 'bg-green-50 text-green-700' : v?.ok === false ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                      {k} {v?.ok === true ? '✓' : v?.ok === false ? '✗' : '?'}
                      {v?.durationSec != null ? ` ${v.durationSec}s` : ''}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Page>
  );
}
